import { useEffect, useRef, useState } from 'react';

type Props = {
  to: number;
  durationMs?: number;
  format?: (n: number) => string;
  className?: string;
};

/**
 * Counts up from 0 to `to` once the element scrolls into view. Reduces motion
 * if the user has prefers-reduced-motion enabled.
 */
export default function CountUp({ to, durationMs = 1400, format, className = '' }: Props) {
  const ref = useRef<HTMLSpanElement>(null);
  const [val, setVal] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setVal(to);
      return;
    }
    const io = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const start = performance.now();
            const tick = (now: number) => {
              const t = Math.min(1, (now - start) / durationMs);
              const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
              setVal(to * eased);
              if (t < 1) requestAnimationFrame(tick);
              else setVal(to);
            };
            requestAnimationFrame(tick);
          }
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [to, durationMs]);

  const display = format ? format(val) : Math.round(val).toLocaleString();
  return (
    <span ref={ref} className={`num-tab ${className}`}>
      {display}
    </span>
  );
}
